"use client"

import { useState, useEffect } from "react"
import FormBuilder from "./form-builder"
import FormRenderer from "./form-renderer"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Button } from "../../components/ui/button"
import { PlusCircle } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import {
  createCustomForm,
  deleteCustomForm,
  getBusinessForms,
  updateCustomForm,
} from "../../../ApiCalls/customForms";
import { connect } from "react-redux";
import { selectAuthState } from "../../../store/selectors/authSelectors";

function FormBuilderApp(props) {
  const [forms, setForms] = useState([]);
  const [currentForm, setCurrentForm] = useState(null);
  const [activeTab, setActiveTab] = useState("builder");
  const [loading, setLoading] = useState(true);
  const [updatingForm, setUpdatingForm] = useState(false);
  const [deletingForm, setDeletingForm] = useState(false);

  // Load forms from backend
  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save forms to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("forms", JSON.stringify(forms));
  }, [forms]);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const formsRes = await getBusinessForms(
        props.token,
        props.mantisBusiness._id
      );
      if (formsRes.success) {
        setForms(formsRes.forms);
        if (formsRes.forms.length > 0) {
          setCurrentForm(formsRes.forms[0]);
        }
      } else {
        console.log("Error fetching forms");
      }
    } catch (e) {
      console.log("Error fetching forms", e);
    } finally {
      setLoading(false);
    }
  };

  const createNewForm = () => {
    const newForm = {
      id: Date.now().toString(),
      title: "Nuevo Formulario",
      fields: [],
      finalized: false,
      mantisBusinessId: props.mantisBusiness._id,
      createdBy: props.user._id,
      description: "",
    };
    setForms([...forms, newForm]);
    setCurrentForm(newForm);
    setActiveTab("builder");
  };

  // Function to handle changes within the form builder (e.g., title, fields)
  const handleFormChange = (formId, updates) => {
    const updatedForms = forms.map((form) =>
      form._id === formId || form.id === formId ? { ...form, ...updates } : form
    );
    setForms(updatedForms);
    if (
      currentForm &&
      (currentForm._id === formId || currentForm.id === formId)
    ) {
      setCurrentForm({ ...currentForm, ...updates });
    }
  };

  // Function to handle saving the form (calling the API)
  const handleSaveForm = async () => {
    if (!currentForm) return; // Should not happen if save button is visible

    setUpdatingForm(true);
    try {
      if (currentForm._id) {
        //UPDATE EXISTING FORM
        const formRes = await updateCustomForm(
          props.token,
          props.mantisBusiness._id,
          currentForm, // Use currentForm directly
          currentForm._id
        );
        if (formRes.success) {
          // Forms array is already updated by handleFormChange
          // setCurrentForm is already updated by handleFormChange
        } else {
          console.log("Error updating form");
        }
      } else {
        //CREATE NEW FORM
        const formRes = await createCustomForm(
          props.token,
          props.mantisBusiness._id,
          currentForm // Use currentForm directly
        );
        if (formRes.success) {
          // Replace the temporary form with the one from the API response
          let formsCopy = [...forms];
          const formIndex = formsCopy.findIndex(
            (form) => form.id === currentForm.id // Use currentForm.id for lookup
          );
          if (formIndex !== -1) {
            formsCopy[formIndex] = formRes.form;
            setForms(formsCopy);
            setCurrentForm(formRes.form);
          }
        } else {
          console.log("Error creating form");
        }
      }
    } catch (e) {
      console.log("Error saving form", e); // Changed log message
    } finally {
      setUpdatingForm(false);
    }
  };

  const finalizeForm = (updatedForm) => {
    const updatedForms = forms.map((form) =>
      form.id === updatedForm.id ? updatedForm : form
    );
    setForms(updatedForms);
    setCurrentForm(updatedForm);
  };

  const deleteForm = async (formId) => {
    if (deletingForm) return;
    setDeletingForm(true);
    try {
      const formRes = await deleteCustomForm(
        props.token,
        formId,
        props.mantisBusiness._id
      );
      if (formRes.success) {
        //Remove form from forms
        let formsCopy = [...forms];
        const formIndex = formsCopy.findIndex((form) => form._id === formId);
        if (formIndex !== -1) {
          formsCopy.splice(formIndex, 1);
          setForms(formsCopy);
          setCurrentForm(null);
        }
      } else {
        console.log("Error deleting form");
      }
    } catch {
      console.log("Error deleting form");
    } finally {
      setDeletingForm(false);
    }
  };

  const selectForm = (form) => {
    setCurrentForm(form);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="md:col-span-1 space-y-4">
        <Card
          className="shadow-md"
          style={{
            borderRadius: "12px",
            backgroundColor: "var(--doc-bg-secondary)",
            border: "1px solid var(--doc-border)",
          }}
        >
          <CardHeader className="pb-3">
            <CardTitle
              className="text-lg"
              style={{ color: "var(--doc-text-primary)" }}
            >
              Mis Formularios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={createNewForm}
              className="w-full"
              style={{
                borderRadius: "8px",
                backgroundColor: "var(--color-secondary)",
                color: "white",
                border: "none",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Crear Formulario
            </Button>

            {loading ? (
              <div className="text-center">
                <p
                  className="text-muted-foreground"
                  style={{ color: "var(--doc-text-secondary)" }}
                >
                  Cargando formularios...
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {forms.length === 0 ? (
                  <div
                    className="text-center py-8"
                    style={{ color: "var(--doc-text-secondary)" }}
                  >
                    <p className="text-sm">No hay formularios creados</p>
                    <p className="text-xs mt-1">Crea tu primer formulario</p>
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {forms.map((form) => (
                      <li key={form._id || form.id}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-left group"
                          onClick={() => selectForm(form)}
                          style={{
                            borderRadius: "8px",
                            backgroundColor:
                              currentForm?.id === form.id
                                ? "var(--color-secondary)"
                                : "transparent",
                            color:
                              currentForm?.id === form.id
                                ? "white"
                                : "var(--doc-text-primary)",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span className="truncate">{form.title}</span>
                            {form.finalized && (
                              <Badge
                                variant="outline"
                                className="ml-2"
                                style={{
                                  backgroundColor:
                                    "rgba(var(--color-secondary-rgb), 0.1)",
                                  color: "var(--color-secondary)",
                                  border: "1px solid var(--color-secondary)",
                                }}
                              >
                                Finalizado
                              </Badge>
                            )}
                          </div>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {forms.length > 0 && (
          <div
            className="text-xs text-center"
            style={{ color: "var(--doc-text-secondary)" }}
          >
            Los formularios se guardan automáticamente en tu navegador
          </div>
        )}
      </div>

      <div className="md:col-span-3">
        {currentForm ? (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger
                value="builder"
                style={{
                  borderRadius: "8px",
                  "--active-bg": "var(--color-secondary)",
                  "--inactive-bg": "var(--doc-bg-secondary)",
                  "--active-text": "white",
                  "--inactive-text": "var(--doc-text-primary)",
                  backgroundColor:
                    activeTab === "builder"
                      ? "var(--active-bg)"
                      : "var(--inactive-bg)",
                  color:
                    activeTab === "builder"
                      ? "var(--active-text)"
                      : "var(--inactive-text)",
                }}
              >
                Constructor
              </TabsTrigger>
              <TabsTrigger
                value="preview"
                style={{
                  borderRadius: "8px",
                  "--active-bg": "var(--color-secondary)",
                  "--inactive-bg": "var(--doc-bg-secondary)",
                  "--active-text": "white",
                  "--inactive-text": "var(--doc-text-primary)",
                  backgroundColor:
                    activeTab === "preview"
                      ? "var(--active-bg)"
                      : "var(--inactive-bg)",
                  color:
                    activeTab === "preview"
                      ? "var(--active-text)"
                      : "var(--inactive-text)",
                }}
              >
                Vista Previa
              </TabsTrigger>
            </TabsList>
            <TabsContent value="builder" className="mt-0">
              <FormBuilder
                form={currentForm}
                onFormChange={handleFormChange}
                onSave={handleSaveForm}
                deleteForm={deleteForm}
                finalizeForm={finalizeForm}
                updatingForm={updatingForm}
                deletingForm={deletingForm}
              />
            </TabsContent>
            <TabsContent value="preview" className="mt-0">
              <div
                className="bg-muted/20 p-6 rounded shadow-md"
                style={{
                  backgroundColor: "var(--doc-bg-tertiary)",
                  borderRadius: "12px",
                }}
              >
                <FormRenderer form={currentForm} isPreview={true} />
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <div
            className="border rounded p-12 text-center bg-muted/10"
            style={{
              backgroundColor: "var(--doc-bg-secondary)",
              borderRadius: "12px",
              border: "1px solid var(--doc-border)",
            }}
          >
            <div className="max-w-md mx-auto">
              <h3
                className="text-xl font-medium mb-2"
                style={{ color: "var(--doc-text-primary)" }}
              >
                Comienza a crear formularios
              </h3>
              <p
                className="text-muted-foreground mb-6"
                style={{ color: "var(--doc-text-secondary)" }}
              >
                Crea formularios personalizados con campos de texto, selección,
                documentos y más.
              </p>
              <Button
                onClick={createNewForm}
                size="lg"
                className="px-8"
                style={{
                  borderRadius: "8px",
                  backgroundColor: "var(--color-secondary)",
                  color: "white",
                  border: "none",
                  boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
                }}
              >
                <PlusCircle className="mr-2 h-5 w-5" />
                Crear mi primer formulario
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default connect(selectAuthState)(FormBuilderApp);